'use client';

import { useLayoutEffect, useRef, useState, type FC, type ReactNode } from 'react';
import { Button } from '../../../components/ui/Button';
import { BottomSheet } from '../../../components/ui/BottomSheet';
import { FaSlidersH } from '../../../components/icons';
import { takeWikiTabsAnchor } from './WikiCatalogNav';

interface WikiFiltersRowProps {
  tabs: ReactNode;
  pageHref: string;
  label: string;
  closeLabel: string;
  count: number;
  children: ReactNode;
}

// WHY: край ленты вкладок на телефоне растворяется маской на 24px (WikiCategoryTabs) — вкладка под ней читается как спрятанная
const EDGE_FADE_PX = 24;

const centerTab = (nav: HTMLElement, tab: HTMLElement, behavior: ScrollBehavior) => {
  const tabBox = tab.getBoundingClientRect();
  const navBox = nav.getBoundingClientRect();
  nav.scrollBy({ left: tabBox.left - navBox.left - (navBox.width - tabBox.width) / 2, behavior });
};

const tabInView = (nav: HTMLElement, tab: HTMLElement) => {
  const tabBox = tab.getBoundingClientRect();
  const navBox = nav.getBoundingClientRect();
  return tabBox.left >= navBox.left + EDGE_FADE_PX && tabBox.right <= navBox.right - EDGE_FADE_PX;
};

// WHY: на телефоне вкладки категорий и кнопка фильтров в одной строке, форма открывается в шторке только по нажатию — сетка остаётся на месте, кнопки шторки не прячутся под таб-баром
export const WikiFiltersRow: FC<WikiFiltersRowProps> = ({ tabs, pageHref, label, closeLabel, count, children }) => {
  const [open, setOpen] = useState(false);
  const tabsRef = useRef<HTMLDivElement>(null);
  const placedRef = useRef(false);

  // WHY: над вкладками у каждой категории шапка своей высоты — после перехода по вкладке экран докручивается до кадра отрисовки,
  // чтобы ряд встал на ту же высоту экрана, где его нажали, а лента — на ту же прокрутку; иначе страница прыгает.
  // При открытии страницы выбранная категория выезжает в середину ленты: иначе прячется за кнопкой фильтров
  useLayoutEffect(() => {
    const anchor = takeWikiTabsAnchor(pageHref);
    const nav = tabsRef.current?.querySelector('nav');
    const tab = nav?.querySelector<HTMLElement>('[aria-current="page"]');
    if (!nav || !tab) return;
    const scroller = nav.closest<HTMLElement>('[data-screen-scroll]');
    if (anchor && scroller) {
      scroller.scrollTop += nav.getBoundingClientRect().top - anchor.top;
      nav.scrollLeft = anchor.left;
      if (!tabInView(nav, tab)) centerTab(nav, tab, 'smooth');
    } else if (!placedRef.current) {
      centerTab(nav, tab, 'auto');
    }
    placedRef.current = true;
  }, [pageHref]);

  return (
    <div className="flex items-center gap-2">
      <div ref={tabsRef} className="min-w-0 flex-1">
        {tabs}
      </div>
      <Button
        type="button"
        variant="tertiary"
        size="m"
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={() => setOpen(true)}
        className="h-11 shrink-0 gap-1.5 px-3.5 font-semibold lg:hidden"
      >
        <FaSlidersH aria-hidden size={14} className="text-label-secondary" />
        {label}
        {count > 0 && <span className="rounded-full bg-accent-blue px-2 text-[13px] font-semibold leading-[20px] text-white">{count}</span>}
      </Button>
      <BottomSheet isOpen={open} onClose={() => setOpen(false)} title={label} closeAriaLabel={closeLabel} heightClass="h-[88dvh]" bodyClassName="pt-1">
        {/* WHY: форма уходит обычным GET-переходом на новый адрес — шторку закрываем сразу, не дожидаясь новой страницы */}
        <div onSubmit={() => setOpen(false)} className="flex min-h-full flex-col">
          {children}
        </div>
      </BottomSheet>
    </div>
  );
};
